// TMOS Page Context Wrapper
// Date: December 16, 2025
// Purpose: Provides visual differentiation for roadmap-related pages
//          - Marketplace (blue) - browsing/discovery
//          - Template Builder (purple) - template creation
//          - Tenant Roadmap Builder (green) - workspace roadmaps

import React from 'react';
import { useLocation } from 'react-router-dom';
import { ShoppingBagIcon, DocumentTextIcon, MapIcon } from '@heroicons/react/24/outline';

export type PageContext = 'marketplace' | 'template-builder' | 'tenant-roadmap-builder';

interface PageContextWrapperProps {
  children: React.ReactNode;
  context?: PageContext; // Optional override, otherwise auto-detected
}

interface ContextConfig {
  name: string;
  badgeColor: string;
  badgeBg: string;
  borderColor: string;
  icon: React.ComponentType<{ className?: string }>;
  headerBg: string;
  buttonColor: string;
  buttonHover: string;
}

const contextConfigs: Record<PageContext, ContextConfig> = {
  'marketplace': {
    name: 'Marketplace',
    badgeColor: 'text-blue-700',
    badgeBg: 'bg-blue-100',
    borderColor: 'border-blue-500',
    icon: ShoppingBagIcon,
    headerBg: 'bg-blue-50',
    buttonColor: 'bg-blue-600',
    buttonHover: 'hover:bg-blue-700'
  },
  'template-builder': {
    name: 'Template Builder',
    badgeColor: 'text-purple-700',
    badgeBg: 'bg-purple-100',
    borderColor: 'border-purple-500',
    icon: DocumentTextIcon,
    headerBg: 'bg-purple-50',
    buttonColor: 'bg-purple-600',
    buttonHover: 'hover:bg-purple-700'
  },
  'tenant-roadmap-builder': {
    name: 'My Roadmap',
    badgeColor: 'text-green-700',
    badgeBg: 'bg-green-100',
    borderColor: 'border-green-500',
    icon: MapIcon,
    headerBg: 'bg-green-50',
    buttonColor: 'bg-green-600',
    buttonHover: 'hover:bg-green-700'
  }
};

/**
 * Auto-detect page context from URL path
 */
const detectContext = (pathname: string): PageContext => {
  if (pathname.includes('/marketplace/')) {
    return 'marketplace';
  }
  if (pathname.includes('/tenant/roadmaps/builder')) {
    return 'tenant-roadmap-builder';
  }
  if (pathname.includes('/templates/comprehensive-builder')) {
    return 'template-builder';
  }
  // Default fallback
  return 'marketplace';
};

export const PageContextWrapper: React.FC<PageContextWrapperProps> = ({ 
  children, 
  context 
}) => {
  const location = useLocation();
  const detectedContext = context || detectContext(location.pathname);
  const config = contextConfigs[detectedContext];
  const Icon = config.icon;

  return (
    <div className="relative">
      {/* Top Context Banner - No borders */}
      <div className={`${config.headerBg} py-2 px-4`}>
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Icon className={`w-5 h-5 ${config.badgeColor}`} />
          <span className={`text-sm font-semibold ${config.badgeColor}`}>
            {config.name}
          </span>
        </div>
      </div>

      {/* Main Content - No left border */}
      <div>
        {children}
      </div>
    </div>
  );
};

/**
 * Hook to get current page context configuration
 */
export const usePageContext = (): { context: PageContext; config: ContextConfig } => {
  const location = useLocation();
  const context = detectContext(location.pathname);
  return {
    context,
    config: contextConfigs[context]
  };
};

/**
 * Get button classes for context-specific styling
 */
export const getContextButtonClasses = (context: PageContext): string => {
  const config = contextConfigs[context];
  return `${config.buttonColor} ${config.buttonHover} text-white`;
};

